"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { DEFAULT_BELLS, DEFAULT_WEEK, SUBJECT_CHIPS, compactHhmm, shortSubject, WEEKDAY_SHORT } from "@/lib/timetable-core";
import type { LessonDto, TimetableResponse } from "@/lib/types";
import { cn } from "@/lib/utils";

type DraftBell = { start: string; end: string };
type Cell = { weekday: number; slotOrder: number };

export function TimetableEditor({ onDone }: { onDone?: () => void }) {
  const [bells, setBells] = useState<DraftBell[]>([]);
  const [lessons, setLessons] = useState<LessonDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cell, setCell] = useState<Cell | null>(null);
  const [custom, setCustom] = useState("");

  async function load() {
    const data = await api<TimetableResponse>("/api/timetable");
    setBells(data.bells.map((bell) => ({ start: bell.start, end: bell.end })));
    setLessons(data.lessons);
    setLoading(false);
  }

  useEffect(() => {
    void load().catch((error) => {
      toast.error(error instanceof Error ? error.message : "Не вдалося завантажити розклад.");
      setLoading(false);
    });
  }, []);

  function titleAt(weekday: number, slotOrder: number) {
    return lessons.find((lesson) => lesson.weekday === weekday && lesson.slotOrder === slotOrder)?.title ?? "";
  }

  function setTitle(weekday: number, slotOrder: number, title: string) {
    setLessons((current) => {
      const next = current.filter((lesson) => !(lesson.weekday === weekday && lesson.slotOrder === slotOrder));
      const trimmed = title.trim();
      if (trimmed) next.push({ weekday, slotOrder, title: trimmed });
      return next;
    });
  }

  async function save() {
    setSaving(true);
    try {
      await api("/api/timetable", {
        method: "PATCH",
        body: JSON.stringify({ bells, lessons }),
      });
      toast.success("Розклад збережено.");
      await load();
      onDone?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося зберегти розклад.");
    } finally {
      setSaving(false);
    }
  }

  function applySchoolBells() {
    setBells(DEFAULT_BELLS.map((bell) => ({ start: bell.start, end: bell.end })));
  }

  function applyNotebook() {
    setBells(DEFAULT_BELLS.map((bell) => ({ start: bell.start, end: bell.end })));
    setLessons(
      Object.entries(DEFAULT_WEEK).flatMap(([weekday, titles]) =>
        titles.map((title, index) => ({
          weekday: Number(weekday),
          slotOrder: index + 1,
          title,
        })),
      ),
    );
  }

  function copyMonday() {
    const monday = lessons.filter((lesson) => lesson.weekday === 1);
    setLessons((current) => {
      const others = current.filter((lesson) => lesson.weekday === 1);
      for (const weekday of [2, 3, 4, 5]) {
        for (const lesson of monday) {
          others.push({ weekday, slotOrder: lesson.slotOrder, title: lesson.title });
        }
      }
      return others;
    });
  }

  function addSlot() {
    if (bells.length >= 8) return;
    const last = bells[bells.length - 1];
    setBells([...bells, { start: last?.end || "13:15", end: last?.end || "14:00" }]);
  }

  function removeLastSlot() {
    if (bells.length <= 1) return;
    const lastOrder = bells.length;
    setBells(bells.slice(0, -1));
    setLessons((current) => current.filter((lesson) => lesson.slotOrder !== lastOrder));
  }

  if (loading) {
    return <p className="text-xs text-muted-foreground">Завантажуємо розклад…</p>;
  }

  return (
    <section className="space-y-4 rounded-3xl bg-card p-4 ring-1 ring-foreground/8">
      <div>
        <h2 className="text-sm font-medium">Розклад і дзвінки</h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">
          Один набір дзвінків на весь клас. Тапніть клітинку, щоб поставити предмет. Порожня — уроку немає.
        </p>
      </div>

      <div className="space-y-2">
        {bells.map((bell, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="w-6 shrink-0 text-sm font-semibold">{index + 1}</span>
            <Input
              type="time"
              value={bell.start}
              onChange={(event) => {
                const next = [...bells];
                next[index] = { ...bell, start: event.target.value };
                setBells(next);
              }}
            />
            <span className="text-muted-foreground">–</span>
            <Input
              type="time"
              value={bell.end}
              onChange={(event) => {
                const next = [...bells];
                next[index] = { ...bell, end: event.target.value };
                setBells(next);
              }}
            />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" className="rounded-2xl" onClick={addSlot} disabled={bells.length >= 8}>
          Додати урок
        </Button>
        <Button type="button" variant="outline" className="rounded-2xl" onClick={removeLastSlot} disabled={bells.length <= 1}>
          Прибрати останній
        </Button>
        <Button type="button" variant="outline" className="rounded-2xl" onClick={applySchoolBells}>
          Дзвінки як у школі
        </Button>
        <Button type="button" variant="outline" className="rounded-2xl" onClick={applyNotebook}>
          Як у зошиті
        </Button>
        <Button type="button" variant="outline" className="rounded-2xl" onClick={copyMonday}>
          Як понеділок
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-separate border-spacing-1 text-center">
          <thead>
            <tr>
              <th className="w-9" />
              {[1, 2, 3, 4, 5].map((weekday) => (
                <th key={weekday} className="text-[11px] font-semibold text-muted-foreground">
                  {WEEKDAY_SHORT[weekday]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bells.map((bell, index) => {
              const slotOrder = index + 1;
              return (
                <tr key={slotOrder}>
                  <th className="w-9 text-left align-top text-[9px] font-medium leading-tight text-muted-foreground">
                    <span className="block text-[10px] font-semibold text-foreground">{slotOrder}</span>
                    <span className="block tabular-nums">{compactHhmm(bell.start)}</span>
                    <span className="block tabular-nums">{compactHhmm(bell.end)}</span>
                  </th>
                  {[1, 2, 3, 4, 5].map((weekday) => {
                    const title = titleAt(weekday, slotOrder);
                    return (
                      <td key={weekday}>
                        <button
                          type="button"
                          className={cn(
                            "flex h-12 w-full items-center justify-center rounded-xl px-0.5 text-[11px] leading-snug ring-1 ring-foreground/10",
                            title ? "bg-accent font-medium" : "text-muted-foreground",
                          )}
                          onClick={() => {
                            setCell({ weekday, slotOrder });
                            setCustom("");
                          }}
                        >
                          {title ? shortSubject(title) : "—"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Button className="h-11 w-full rounded-2xl" disabled={saving} onClick={() => void save()}>
        Зберегти розклад
      </Button>
      {onDone ? (
        <Button type="button" variant="outline" className="h-11 w-full rounded-2xl" onClick={onDone}>
          Готово
        </Button>
      ) : null}

      <Dialog open={Boolean(cell)} onOpenChange={(open) => !open && setCell(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {cell ? `${WEEKDAY_SHORT[cell.weekday]} · ${cell.slotOrder} урок` : "Предмет"}
            </DialogTitle>
            <DialogDescription>Оберіть предмет або впишіть свій.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {SUBJECT_CHIPS.map((subject) => (
              <button
                key={subject}
                type="button"
                className={cn(
                  "rounded-full px-3 py-1.5 text-sm ring-1 ring-foreground/10",
                  cell && titleAt(cell.weekday, cell.slotOrder) === subject
                    ? "bg-primary text-primary-foreground"
                    : "bg-accent",
                )}
                onClick={() => {
                  if (!cell) return;
                  setTitle(cell.weekday, cell.slotOrder, subject);
                  setCell(null);
                }}
              >
                {subject}
              </button>
            ))}
          </div>
          <Input
            value={custom}
            onChange={(event) => setCustom(event.target.value)}
            placeholder="Свій предмет"
            onKeyDown={(event) => {
              if (event.key === "Enter" && cell && custom.trim()) {
                setTitle(cell.weekday, cell.slotOrder, custom);
                setCell(null);
              }
            }}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 rounded-2xl"
              onClick={() => {
                if (!cell) return;
                setTitle(cell.weekday, cell.slotOrder, "");
                setCell(null);
              }}
            >
              Немає уроку
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-2xl"
              disabled={!custom.trim()}
              onClick={() => {
                if (!cell || !custom.trim()) return;
                setTitle(cell.weekday, cell.slotOrder, custom);
                setCell(null);
              }}
            >
              Поставити
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
